import {
  NgTemplateOutlet } from '@angular/common';
import { Component,
  computed,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { ControlValueAccessor, FormsModule } from '@angular/forms';
import { TranslateDirective } from '@ngx-translate/core';

import { ValueAccessorProviderFactory } from '../../../components/value-accessor/value-accessor-provider-factory';
import { IdService } from '../../../services/id.service';
import { DropdownComponent } from '../../../components/dropdown/dropdown.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { DropdownItemComponent } from '../../../components/dropdown/dropdown-item.component';
import { DropdownToggleDirective } from '../../../components/dropdown/dropdown-toggle.directive';
import { Account } from '../../../domain/account.domain';
import { AccountLabelComponent } from './account-label.component';

@Component({
  selector: 'td-wb-account-selector',
  templateUrl: 'account-selector.component.html',
  styleUrls: ['account-selector.component.scss'],
  providers: [ValueAccessorProviderFactory(AccountSelectorComponent)],
  imports: [
    DropdownComponent,
    DropdownToggleDirective,
    TranslatePipe,
    TranslateDirective,
    FormsModule,
    NgTemplateOutlet,
    DropdownItemComponent,
    AccountLabelComponent
  ]
})
export class AccountSelectorComponent implements ControlValueAccessor {
  private readonly idService = inject(IdService);
  private readonly valueState = signal<Account | null | undefined>(undefined);
  private readonly disabledByCva = signal(false);
  private onChangeCallback: (value: unknown) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};

  static readonly DEFAULT_DROP_DOWN_MAX_SIZE: number = 10;
  private static readonly ID_PREFIX: string = 'td-wb-dropdown-account-selector';

  uniqueId: string;
  readonly accounts = input<Array<Account>>(undefined);
  readonly size = input<number>(AccountSelectorComponent.DEFAULT_DROP_DOWN_MAX_SIZE);

  readonly showPlaceholder = input<boolean>(false);

  readonly disabled = input<boolean>(undefined);
  readonly isDisabled = computed(() => this.disabled() || this.disabledByCva());

  readonly account = input<Account>(undefined);
  readonly selectedAccount = computed(() => this.valueState() ?? this.account());
  readonly accountChange = output<Account>();

  constructor() {
    this.uniqueId = this.idService.generateNamespaceSequentialId(AccountSelectorComponent.ID_PREFIX);
  }

  writeValue(value: Account | null | undefined): void {
    this.valueState.set(value);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByCva.set(isDisabled);
  }

  onModelChange($event: Account): void {
    this.valueState.set($event);
    this.onChangeCallback($event);
    this.onTouchedCallback();
    this.accountChange.emit($event);
  }

  comparator(key: keyof Account): (a: Record<string, unknown>, b: Record<string, unknown>) => boolean {
    return (a: Record<string, unknown>, b: Record<string, unknown>) => {
      const aDefined = a !== undefined && a !== null;
      const bDefined = b !== undefined && b !== null;
      const bothUndefined = (a === undefined && b === undefined);
      return (aDefined && bDefined && a[key] === b[key]) || bothUndefined;
    };
  }

}
